import * as React from "react";
import { Download, Share, X } from "lucide-react";
import { usePwaInstall } from "../hooks/usePwaInstall";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const DISMISS_KEY = "installBannerDismissed";

/**
 * Install-app banner for the mobile shell. Android/Chromium get a one-tap
 * native install prompt via beforeinstallprompt; iOS gets a guided modal
 * (Safari has no programmatic install API). Hidden once the app is installed
 * or the user dismisses the banner.
 */
export function InstallAppBanner() {
  const { isIos, showInstall, promptInstall } = usePwaInstall();
  const [guideOpen, setGuideOpen] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(
    () =>
      typeof window !== "undefined" &&
      window.localStorage.getItem(DISMISS_KEY) === "1",
  );

  if (!showInstall || dismissed) return null;

  return (
    <div className="mb-4 mt-2 flex items-center gap-3 rounded-xl bg-primary px-3 py-2.5 text-primary-foreground shadow-sm">
      <Download className="h-5 w-5 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1 text-sm font-medium leading-snug">
        Install Idarah for one-tap access from your home screen.
      </p>
      <Button
        size="sm"
        variant="secondary"
        className="shrink-0"
        onClick={() => (isIos ? setGuideOpen(true) : promptInstall())}
      >
        Install
      </Button>
      <button
        type="button"
        aria-label="Dismiss install prompt"
        onClick={() => {
          window.localStorage.setItem(DISMISS_KEY, "1");
          setDismissed(true);
        }}
        className="shrink-0 text-primary-foreground/60 hover:text-primary-foreground"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>

      <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Install Idarah</DialogTitle>
            <DialogDescription>
              Safari cannot install apps automatically. Follow these two steps
              to add Idarah to your home screen:
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm text-foreground">
            <li className="flex items-start gap-2">
              <Share className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                Tap the <strong>Share</strong> button (square with an up arrow)
                in Safari&apos;s bottom toolbar.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Download className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                Scroll down and tap <strong>Add to Home Screen</strong>, then{" "}
                <strong>Add</strong>. Idarah will open full screen from the new
                icon on your home screen.
              </span>
            </li>
          </ol>
        </DialogContent>
      </Dialog>
    </div>
  );
}
